import { useCallback, useEffect, useState } from 'react';
import { HelpCircle, Check, Pencil, Trash2, ChevronLeft, Loader2, Sparkles } from 'lucide-react';
import { Link } from 'react-router-dom';
import api from '@/lib/api';
import MusicCardCompact from '@/components/MusicCardCompact';
import MusicCardDetail from '@/components/MusicCardDetail';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { toast } from 'sonner';

/**
 * Review
 * ---------------------------------------------------------------------------
 * Inbox of cards flagged `needs_review=true` — these are tracklist commits
 * that came through with the user's OCR text intact but couldn't be strictly
 * verified against iTunes or Spotify (obscure indie / boot / private-press
 * tracks etc.).
 *
 * Three actions per row:
 *   • **Approve as-is** → POST /cards/{id}/approve-review — clears the flag.
 *     Card stays in whatever crate it's currently in (target_bucket picked
 *     at commit time).
 *   • **Edit & re-enrich** → PATCH /cards/{id} with corrected artist/title.
 *     The backend re-runs enrichment; if it succeeds the card becomes fully
 *     active and needs_review flips to false automatically.
 *   • **Delete** → soft-delete via DELETE /cards/{id}.
 *
 * A bulk "Approve all" button is also available for users who trust their
 * OCR and just want to clear the queue.
 */
export default function Review() {
  const [cards, setCards] = useState([]);
  const [loading, setLoading] = useState(true);

  // Edit modal
  const [editing, setEditing] = useState(null);
  const [editTitle, setEditTitle] = useState('');
  const [editArtist, setEditArtist] = useState('');
  const [saving, setSaving] = useState(false);

  // Approve-all bulk state
  const [approvingAll, setApprovingAll] = useState(false);

  // Full-card detail modal (so users can preview / play / etc. before
  // deciding). Follows the same pattern as Owned / Album / Crate.
  const [selected, setSelected] = useState(null);
  const [previewingId, setPreviewingId] = useState(null);

  const fetch = useCallback(async () => {
    setLoading(true);
    try {
      // needs_review filter added to /api/cards on the backend
      const { data } = await api.get('/cards', {
        params: { needs_review: true, status: 'active' },
      });
      setCards(data.cards || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load review inbox');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetch(); }, [fetch]);

  const openEdit = (card) => {
    setEditing(card);
    setEditTitle(card.title || '');
    setEditArtist(card.artist || '');
    setSelected(null);
  };

  const submitEdit = async (e) => {
    e.preventDefault();
    if (!editing) return;
    if (!editTitle.trim() || !editArtist.trim()) {
      toast.error('Title and artist required');
      return;
    }
    setSaving(true);
    try {
      // PATCH /cards/{id} re-runs enrichment. If enrichment succeeds we
      // expect the backend to flip needs_review back to false as a side
      // effect (currently just re-enriches — we approve here as a
      // belt-and-suspenders to be sure the card leaves this inbox).
      await api.patch(`/cards/${editing.id}`, {
        title: editTitle.trim(),
        artist: editArtist.trim(),
      });
      // Always clear the review flag once the user has actively edited
      // and saved — they've confirmed the metadata is correct.
      try {
        await api.post(`/cards/${editing.id}/approve-review`);
      } catch (_) {
        // Non-fatal — the PATCH already saved the edit.
      }
      toast.success(`Saved: ${editArtist.trim()} \u2014 ${editTitle.trim()}`);
      setEditing(null);
      fetch();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleApprove = async (card) => {
    try {
      await api.post(`/cards/${card.id}/approve-review`);
      toast.success('Approved \u2014 keeping as-is');
      setSelected(null);
      fetch();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Approval failed');
    }
  };

  const handleDelete = async (card) => {
    if (!window.confirm(`Delete "${card.artist} \u2014 ${card.title}"?`)) return;
    try {
      await api.delete(`/cards/${card.id}`);
      toast.success('Deleted');
      setSelected(null);
      fetch();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to delete');
    }
  };

  const handleApproveAll = async () => {
    if (cards.length === 0) return;
    if (!window.confirm(`Approve all ${cards.length} tracks as-is? (They\u2019ll stay in their current crates.)`)) return;
    setApprovingAll(true);
    let approved = 0;
    let failed = 0;
    for (const c of cards) {
      try {
        await api.post(`/cards/${c.id}/approve-review`);
        approved += 1;
      } catch (_) {
        failed += 1;
      }
    }
    setApprovingAll(false);
    if (approved > 0) {
      toast.success(
        `Approved ${approved} track${approved === 1 ? '' : 's'}${failed ? ` \u00b7 ${failed} failed` : ''}`,
      );
    } else if (failed > 0) {
      toast.error(`Failed to approve any tracks (${failed} errors)`);
    }
    fetch();
  };

  return (
    <div data-testid="review-page">
      <div className="flex items-start justify-between gap-3 mb-5 flex-wrap">
        <div className="min-w-0">
          <div className="flex items-center gap-3 mb-2">
            <Link
              to="/crate"
              className="text-sm text-muted-foreground hover:text-foreground inline-flex items-center gap-1"
              data-testid="review-back-link"
            >
              <ChevronLeft className="w-4 h-4" /> Back to collection
            </Link>
          </div>
          <h1 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight inline-flex items-center gap-2">
            <HelpCircle className="w-7 h-7 text-[hsl(var(--primary))]" />
            Review inbox
          </h1>
          <p className="text-sm text-muted-foreground mt-1 max-w-2xl">
            These tracklist tracks couldn&rsquo;t be verified against iTunes or Spotify. Approve as-is if your OCR looks right, edit to trigger a fresh search, or delete if they were bad reads.
          </p>
        </div>
        {cards.length > 0 && (
          <Button
            variant="outline"
            size="sm"
            disabled={approvingAll}
            onClick={handleApproveAll}
            data-testid="review-approve-all"
            className="rounded-lg"
          >
            {approvingAll ? (
              <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />
            ) : (
              <Check className="w-4 h-4 mr-1.5" />
            )}
            Approve all as-is
          </Button>
        )}
      </div>

      {loading ? (
        <div className="space-y-2 max-w-4xl mx-auto">
          {[1, 2, 3].map((i) => (
            <Skeleton key={`review-skeleton-${i}`} className="h-20 rounded-xl" />
          ))}
        </div>
      ) : cards.length === 0 ? (
        <div
          className="rounded-2xl border border-dashed border-border bg-card p-10 text-center max-w-2xl mx-auto"
          data-testid="review-empty"
        >
          <Sparkles className="w-10 h-10 mx-auto text-[hsl(var(--primary))] mb-2" />
          <h3 className="font-display text-lg font-semibold">Nothing to review</h3>
          <p className="text-sm text-muted-foreground">
            Every committed track was verified against iTunes or Spotify. Nice work.
          </p>
          <Link
            to="/upload/tracklist"
            className="inline-flex items-center gap-1.5 mt-4 text-xs font-mono text-[hsl(var(--primary))] hover:underline"
            data-testid="review-empty-tracklist-link"
          >
            Parse another tracklist &rarr;
          </Link>
        </div>
      ) : (
        <ul className="space-y-2 max-w-4xl mx-auto" data-testid="review-cards-list">
          {cards.map((card) => (
            <li key={card.id} className="relative">
              <MusicCardCompact
                card={card}
                onOpen={setSelected}
                onMarkOwned={undefined}
                onDelete={handleDelete}
                isPreviewing={previewingId === card.id}
                onPreviewToggle={() => setPreviewingId((id) => (id === card.id ? null : card.id))}
              />
              {/* Row-level primary actions: Approve + Edit. Placed under
                  the compact card so they're always visible without needing
                  the "..." menu. Delete stays in the "..." menu on the card
                  itself since it's a destructive action. */}
              <div className="mt-1 flex flex-wrap gap-2 pl-[74px] sm:pl-[84px] pr-1 pb-1">
                <Button
                  size="sm"
                  onClick={() => handleApprove(card)}
                  data-testid={`review-approve-${card.id}`}
                  className="rounded-lg h-8 text-xs"
                >
                  <Check className="w-3.5 h-3.5 mr-1.5" />
                  Approve as-is
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => openEdit(card)}
                  data-testid={`review-edit-${card.id}`}
                  className="rounded-lg h-8 text-xs"
                >
                  <Pencil className="w-3.5 h-3.5 mr-1.5" />
                  Edit &amp; re-enrich
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => handleDelete(card)}
                  data-testid={`review-delete-${card.id}`}
                  className="rounded-lg h-8 text-xs text-muted-foreground hover:text-[hsl(var(--destructive))]"
                >
                  <Trash2 className="w-3.5 h-3.5 mr-1.5" />
                  Delete
                </Button>
              </div>
            </li>
          ))}
        </ul>
      )}

      {/* Edit dialog — mirrors the Unidentified "Fix this pull" flow but
          without the screenshot preview (there's no screenshot for
          tracklist rows). */}
      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-w-md bg-card border-border" data-testid="review-edit-dialog">
          <DialogHeader>
            <DialogTitle className="font-display">Edit this track</DialogTitle>
            <DialogDescription>
              We&rsquo;ll re-search iTunes + Spotify with your corrections. Enrichment (album art, preview, BPM) will fill in if we find a strict match.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={submitEdit} className="space-y-3">
            <div>
              <Label htmlFor="review-artist">Artist</Label>
              <Input
                id="review-artist"
                value={editArtist}
                onChange={(e) => setEditArtist(e.target.value)}
                placeholder="e.g. Daft Punk"
                data-testid="review-edit-artist"
                required
              />
            </div>
            <div>
              <Label htmlFor="review-title">Title</Label>
              <Input
                id="review-title"
                value={editTitle}
                onChange={(e) => setEditTitle(e.target.value)}
                placeholder="e.g. Get Lucky"
                data-testid="review-edit-title"
                required
              />
            </div>
            <DialogFooter className="gap-2">
              <Button
                type="button"
                variant="ghost"
                onClick={() => setEditing(null)}
                className="rounded-lg"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={saving}
                className="rounded-lg"
                data-testid="review-edit-submit"
              >
                {saving ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : null}
                Save &amp; re-enrich
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Full card detail modal — read-only preview / play so users can
          decide before approving. */}
      <MusicCardDetail
        card={selected}
        open={!!selected}
        onClose={() => setSelected(null)}
        onDelete={handleDelete}
      />
    </div>
  );
}
